import '@fortawesome/fontawesome-free/css/all.css';
import './product-unit-table.scss';
import { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import {
  DisplayState,
  EquipmentListingResponse,
  HighwayType,
  ListOnBoomAndBucketRequest,
  Make,
  Model,
  Product,
  ProductInventoryUnit,
  ProductUnitValuation,
  UpdateProductUnitRequest,
} from '../../../../clients/quipli-service/product/product';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { Location } from '../../../../clients/quipli-service/location/location';
import { DateTime } from 'luxon';
import { ProductSchedule } from '../../../../clients/quipli-service/availability/availability';
import { Tooltip } from '../../../../components/tooltip/tooltip';
import { WorkOrderStatus } from '../../../../clients/quipli-service/service-and-repair/service-and-repair';
import { OrderReturnStatus } from '../../../../clients/quipli-service/order/order';
import { ScheduleTooltip } from '../../../inventory-dashboard/schedule-tooltip/schedule-tooltip';
import { ConfirmModal } from '../../../../components/modal/confirm-modal';
import { Modal } from '../../../../components/modal/modal';
import Select from 'react-select';
import Container from '../../../../components/container/container';
import { ActivateAction, DeactivateAction } from '../../../../components/actions/actions';
import { Button } from '../../../../components/form/button';
import { TenantContext } from '../../../../context/tenant-context';
import classNames from 'classnames';
import { HourReading } from '../../../../clients/quipli-service/hour-reading/hour-reading';
import { Spinner } from '../../../../components/spinner/spinner';
import { toast } from '../../../../util/toasts';
import { Typography } from '../../../../components/typography/typography';
import { Configuration } from '../../../../clients/quipli-service/configuration/configuration';
import Icon from '../../../../components/icons/icon';
import { debounce, Dictionary, keyBy } from 'lodash';
import { displayMoneyFormat } from '../../../../util/util';
import { Field } from '../../../../components/field/field';
import { PlacesAutocomplete } from '../../../../components/field/places-autocomplete';
import { Dropdown } from '../../../../components/dropdown/dropdown';
import { Radio } from '../../../../components/radio/radio';
import { DatePicker } from '../../../../components/date-picker/date-picker';
import { Checkbox } from '../../../../components/checkbox/checkbox';

export const ProductUnitTable = ({
  theProduct,
  allLocations,
  configs,
  refreshProduct,
  setTheProduct,
}: {
  theProduct: Product;
  allLocations: Location[];
  configs: Dictionary<Configuration>;
  refreshProduct: () => void;
  setTheProduct: (p: Product) => void;
}) => {
  const { configuration } = useContext(TenantContext);
  const currency = configuration?.currency;

  const {
    getProductSchedules,
    updateProductUnit,
    deleteProductUnit,
    createProductUnit,
    searchMakes,
    getValuationsByProductUnitIds,
  } = useAPI();
  const [product, setProduct] = useState<Product>(theProduct);
  const [locations, setLocations] = useState<Location[]>();
  const [schedule, setProductSchedule] = useState<ProductSchedule>();
  const [actionModal, setActionModal] = useState<ReactNode>();
  const [isHourTrackingOpen, setIsHourTrackingOpen] = useState<boolean>(false);
  const [unitHourTrackingId, setUnitHourTrackingId] = useState<number>();
  const [makes, setMakes] = useState<Make[]>([]);
  const [valuations, setValuations] = useState<Dictionary<ProductUnitValuation>>();

  const [includeSoldUnits, setIncludeSoldUnits] = useState<boolean>(false);

  const qrCodesEnabled = configs['CONF_ENABLE_QR_CODE']?.value === '1';
  const workOrdersEnabled = configs['CONF_ENABLE_SERVICE_AND_REPAIR']?.value === '1';
  const unitTrackingField = configs['CONF_UNIT_TRACKING_VIA']?.value === '1' ? 'serialNumber' : 'unitNumber';
  const makeModelEnabled = configs['CONF_BOOM_AND_BUCKET_INTEGRATION']?.value === '1';
  const saleSerialTrackingEnabled = configs['CONF_SALE_SERIAL_TRACKING']?.value === '1';
  function refresh() {
    Promise.all([
      getProductSchedules(
        [product.id],
        undefined,
        DateTime.now(),
        DateTime.now().plus({ years: 1 }),
        includeSoldUnits,
        includeSoldUnits,
      ),
      makeModelEnabled ? searchMakes() : Promise.resolve([]),
      makeModelEnabled ? getValuationsByProductUnitIds(product.inventoryUnits.map((u) => u.id)) : Promise.resolve([]),
      refreshProduct(),
    ]).then(([schedules, _makes, _valuations]) => {
      setProductSchedule(schedules[0]);
      setMakes(_makes);
      setValuations(keyBy(_valuations, 'productUnitId'));
    });
  }

  useEffect(() => {
    refresh();
  }, [includeSoldUnits]);

  useEffect(() => {
    setLocations(allLocations.filter((l) => theProduct.locationIds.indexOf(l.id) >= 0));
    setProduct(theProduct);
  }, [theProduct, allLocations]);

  function unitStatus(unitId: number): ReactNode {
    const unit = schedule?.product.inventoryUnits.find((u) => u.id === unitId);
    if (!unit) return undefined;
    if (unit.soldAt) {
      return (
        <div className="unit-status hover:cursor-default">
          <div className="status-label sold">Sold</div>
        </div>
      );
    }
    const unitSchedule = schedule?.unitSchedules.find((us) => us.unit.id === unitId);
    if (unitSchedule) {
      const sortedSchedule = unitSchedule.schedule
        .sort((a, b) => a.from.diff(b.from).milliseconds)
        .filter((s) => {
          switch (s.type) {
            case 'WorkOrder':
              return [WorkOrderStatus.InProgress, WorkOrderStatus.OnHold].indexOf(s.workOrderStatus) >= 0;
            case 'Order':
              return (
                OrderReturnStatus.OnRent === s.orderReturnStatus || OrderReturnStatus.OffRent === s.orderReturnStatus
              );
          }
        });
      let label;
      let nextScheduled;
      if (sortedSchedule.length === 0) {
        if (workOrdersEnabled || !product?.inventoryUnits.find((u) => u.id === unitId)?.isOnOutage) {
          label = 'Available';
        } else {
          label = 'Outage';
        }
      } else {
        nextScheduled = sortedSchedule[0];
        switch (nextScheduled.type) {
          case 'WorkOrder':
            label = 'Hard Down';
            break;
          case 'Sold':
            label = 'Sold';
            break;
          case 'NotInService':
            label = 'Not In Service';
            break;
          case 'Order':
            label = nextScheduled.orderReturnStatus === OrderReturnStatus.OffRent ? 'Off Rent' : 'On Rent';
            break;
        }
      }
      const content = (
        <div className="unit-status">
          <div className={`status-label ${label.toLowerCase().replace(' ', '-')}`}>{label}</div>
          {nextScheduled?.to && label !== 'Off Rent' && (
            <div
              className={classNames('status-until', {
                'is-past-due': nextScheduled.to.diff(DateTime.now()).milliseconds < 0,
              })}
            >
              Until: {nextScheduled.to?.toFormat('D t')}
            </div>
          )}
        </div>
      );

      return nextScheduled ? (
        <Tooltip tooltip={<ScheduleTooltip scheduleEntry={nextScheduled} />}>{content}</Tooltip>
      ) : (
        content
      );
    } else {
      return undefined;
    }
  }

  function nextEvent(unitId: number): ReactNode {
    const unitSchedule = schedule?.unitSchedules.find((us) => us.unit.id === unitId);
    if (unitSchedule) {
      const sortedSchedule = unitSchedule.schedule
        .sort((a, b) => a.from.diff(b.from).milliseconds)
        .filter((s) => {
          switch (s.type) {
            case 'WorkOrder':
              return WorkOrderStatus.NotStarted === s.workOrderStatus;
            case 'Order':
              return OrderReturnStatus.NotStarted === s.orderReturnStatus;
          }
        });
      if (sortedSchedule.length > 0) {
        let label;
        const nextScheduled = sortedSchedule[0];
        switch (nextScheduled.type) {
          case 'WorkOrder':
            label = 'Work Order';
            break;
          case 'Sold':
            label = 'Sold';
            break;
          case 'Order':
            label = 'Order';
            break;
          case 'NotInService':
            label = 'Not In Service';
            break;
        }

        return (
          <Tooltip tooltip={<ScheduleTooltip scheduleEntry={nextScheduled} />}>
            <div className="unit-next-event">
              <div className={`event-label ${label.toLowerCase().replace(' ', '-')}`}>{label}</div>
              {nextScheduled?.to && (
                <div
                  className={classNames('event-start', {
                    'is-past-due': nextScheduled.from.diff(DateTime.now()).milliseconds < 0,
                  })}
                >
                  At: {nextScheduled.from?.toFormat('D t')}
                </div>
              )}
            </div>
          </Tooltip>
        );
      }
    } else {
      return undefined;
    }
  }

  async function doUpdate(i: number, update: UpdateProductUnitRequest) {
    if (product) {
      const unit = product.inventoryUnits[i];
      try {
        const updated = await updateProductUnit(unit.productId, unit.id, update);
        setTheProduct({
          ...product,
          inventoryUnits: product.inventoryUnits.map((u, idx) => (i === idx ? updated : u)),
          rentalStock:
            unit.isActive !== updated.isActive
              ? product.rentalStock + (updated.isActive ? 1 : -1)
              : product.rentalStock,
        });

        // Refresh schedules to ensure everything is in sync
        refresh();
      } catch (err: any) {
        const errMsg = err.status === 400 && err.message ? err.message.msg ?? err.message : 'Error updating unit!';
        toast(errMsg, 'error');
      }
    }
  }

  return (
    <div className="product-unit-location">
      <div className="top items-center">
        <Typography type="text-xl">Product Serials</Typography>
        <div className="controls">
          {qrCodesEnabled && <GenerateQRCodesInput />}
          <div className="flex flex-end">
            <div
              className="flex flex-row gap-2 cursor-pointer items-center"
              onClick={() => setIncludeSoldUnits((prev) => !prev)}
            >
              <Typography type="text-sm" weight="regular">
                Include Sold Units
              </Typography>
              <Checkbox isChecked={includeSoldUnits} />
            </div>
            <Button
              className="button-primary sm"
              onClick={(e) => {
                e.preventDefault();
                setActionModal(
                  <ProductInventoryUnitModal
                    qrCodesEnabled={qrCodesEnabled}
                    hourTrackingEnabled={product?.allowHourTracking}
                    locations={locations || []}
                    close={() => setActionModal(undefined)}
                    onSubmit={(work) => {
                      createProductUnit(product.id, work)
                        .then(() => refresh())
                        .catch((err) => {
                          const errMsg =
                            err.status === 400 && err.message ? err.message.msg ?? err.message : 'Error creating unit!';
                          toast(errMsg, 'error');
                        });
                      setActionModal(undefined);
                    }}
                    makes={makes}
                    makeModelEnabled={makeModelEnabled}
                    allowForSale={saleSerialTrackingEnabled && product.allowSale}
                    allowForRent={product.allowRent}
                    refreshMakes={() => {
                      searchMakes().then((makes) => setMakes(makes));
                    }}
                  />,
                );
              }}
              dataCy="add_unit"
            >
              <i className="fa fa-plus" />
              Add
            </Button>
          </div>
        </div>
      </div>

      <Container>
        <div className="overflow-scroll">
          {!makeModelEnabled || valuations ? (
            <table className="data-table product-unit-table">
              <thead>
                <tr>
                  <th>Unit No</th>
                  <th>Serial No</th>
                  <th>Location</th>
                  {saleSerialTrackingEnabled && <th>Type</th>}
                  <th>Purchase Price</th>
                  {qrCodesEnabled && <th>QR Code</th>}
                  <th>Status</th>
                  <th>Next Event</th>
                  {product?.allowHourTracking && <th>Last Hour Reading</th>}
                  {makeModelEnabled && (
                    <>
                      <th>Make/Model/Year</th>
                      <th>Market Value</th>
                    </>
                  )}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {locations &&
                  locations.length > 0 &&
                  schedule &&
                  schedule.product.inventoryUnits.map((unit, i) => {
                    const location = locations.find((l) => l.id === unit.locationId);
                    const fmt = Intl.NumberFormat(undefined, {
                      style: 'currency',
                      currency: location?.currency || 'USD',
                    });
                    return (
                      <tr key={unit.id} className={classNames('unit-row', { inactive: !unit.isActive })}>
                        <td>{unit.unitNumber}</td>
                        <td>{unit.serialNumber}</td>
                        <td>{location?.name}</td>
                        {saleSerialTrackingEnabled && (
                          <td>{unit.forRent && unit.forSale ? 'Rental and Sale' : unit.forRent ? 'Rental' : 'Sale'}</td>
                        )}
                        <td>{unit.purchasePrice && fmt.format(unit.purchasePrice)}</td>
                        {qrCodesEnabled && <td>{unit.qrCode}</td>}
                        <td>{unit.isActive && unitStatus(unit.id)}</td>
                        <td>{unit.isActive && nextEvent(unit.id)}</td>
                        {product.allowHourTracking && (
                          <td>
                            {unit.lastHourReading
                              ? `${unit.lastHourReading.hour} - ${unit.lastHourReading.source
                                  .split(/(?=[A-Z])/)
                                  .join(' ')}`
                              : '-'}
                          </td>
                        )}
                        {makeModelEnabled && (
                          <>
                            <td>
                              {unit.classification
                                ? `${unit.classification.make.displayName} ${unit.classification.model.displayName} ${unit.classification.year}`
                                : '-'}
                            </td>
                            <td>
                              {valuations![unit.id] && valuations![unit.id].auctionEstimatedPriceInCents > 0
                                ? `${displayMoneyFormat(
                                    valuations![`${unit.id}`].auctionEstimatedPriceInCents! / 100,
                                    currency,
                                  )}`
                                : valuations![unit.id]
                                ? `${displayMoneyFormat(
                                    valuations![`${unit.id}`].auctionEstimatedPriceInCents ?? 0,
                                    currency,
                                  )}`
                                : '-'}
                            </td>
                          </>
                        )}
                        <td>
                          <div className="button-group items-center">
                            {unit.isActive || unit.isOnOutage ? (
                              <DeactivateAction onClick={() => doUpdate(i, { ...unit, isActive: false })} />
                            ) : (
                              <ActivateAction
                                onClick={() => doUpdate(i, { ...unit, isActive: true, isOnOutage: false })}
                              />
                            )}

                            <Dropdown
                              trigger={() => (
                                <Button className="button-tertiary" data-cy={`customer_menu`}>
                                  <Icon icon="DotsHorizontal" />
                                </Button>
                              )}
                            >
                              <div>
                                <div
                                  className="flex gap-2"
                                  onClick={() => {
                                    setUnitHourTrackingId(unit.id);
                                    setIsHourTrackingOpen(true);
                                  }}
                                >
                                  <Typography type="text-sm" className="text-quipli-black-800">
                                    View hour reading history
                                  </Typography>
                                </div>
                              </div>
                              <div>
                                <div
                                  className="flex gap-2"
                                  onClick={() =>
                                    setActionModal(
                                      <ProductInventoryUnitModal
                                        unit={unit}
                                        qrCodesEnabled={qrCodesEnabled}
                                        hourTrackingEnabled={product.allowHourTracking}
                                        locations={locations.filter((l) => product?.locationIds.indexOf(l.id) >= 0)}
                                        close={() => setActionModal(undefined)}
                                        onSubmit={(work) => {
                                          if (work.hourReading === unit.lastHourReading?.hour) {
                                            work.hourReading = undefined;
                                          }
                                          doUpdate(i, work);
                                          setActionModal(undefined);
                                        }}
                                        makes={makes}
                                        makeModelEnabled={makeModelEnabled}
                                        allowForSale={saleSerialTrackingEnabled && product.allowSale}
                                        allowForRent={product.allowRent}
                                        refreshMakes={() => {
                                          searchMakes().then((makes) => setMakes(makes));
                                        }}
                                      />,
                                    )
                                  }
                                >
                                  <Typography type="text-sm" className="text-quipli-black-800">
                                    Edit
                                  </Typography>
                                </div>
                              </div>

                              <div>
                                <div
                                  className="flex gap-2"
                                  onClick={() =>
                                    setActionModal(
                                      <ConfirmModal
                                        isOpen={true}
                                        close={() => setActionModal(undefined)}
                                        onConfirm={() => {
                                          deleteProductUnit(unit.productId, unit.id);
                                          setTheProduct({
                                            ...product,
                                            inventoryUnits: product?.inventoryUnits.filter((u) => u.id !== unit.id),
                                            rentalStock: product?.rentalStock - 1,
                                          });
                                          setActionModal(undefined);
                                        }}
                                        headerIcon={<i className="fa fa-trash-can delete-icon"></i>}
                                        prompt={`Are you sure you want to delete unit ${unit[unitTrackingField]}?`}
                                      />,
                                    )
                                  }
                                >
                                  <Typography type="text-sm" className="text-quipli-black-800">
                                    Delete
                                  </Typography>
                                </div>
                              </div>
                              {configs['CONF_BOOM_AND_BUCKET_INTEGRATION']?.value === '1' && (
                                <>
                                  {unit.boomAndBucketEquipmentId ? (
                                    <div>
                                      <div
                                        className="flex gap-2"
                                        onClick={() => {
                                          if (!unit.classification || !unit.serialNumber) {
                                            toast(
                                              'Unit must have serial number and year/make/model to be able to list on Boom and Bucket',
                                              'error',
                                            );
                                            return;
                                          }
                                          setActionModal(
                                            <BoomAndBucketListingDetails
                                              productUnit={unit}
                                              close={() => setActionModal(undefined)}
                                            />,
                                          );
                                        }}
                                      >
                                        <span className="gap-2">
                                          <Typography type="text-sm" className="text-quipli-black-800">
                                            View Boom and Bucket Listing Details
                                          </Typography>
                                        </span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div>
                                      <div
                                        className="flex gap-2"
                                        onClick={() => {
                                          if (!unit.classification || !unit.serialNumber) {
                                            toast(
                                              'Unit must have serial number and year/make/model to be able to list on Boom and Bucket',
                                              'error',
                                            );
                                            return;
                                          }
                                          setActionModal(
                                            <ListOnBoomAndBucketModal
                                              productUnit={unit}
                                              configs={configs}
                                              location={locations.find((l) => l.id === unit.locationId)!}
                                              close={() => {
                                                setActionModal(undefined);
                                                refresh();
                                              }}
                                            />,
                                          );
                                        }}
                                      >
                                        <span className="gap-2">
                                          <Typography type="text-sm" className="text-quipli-black-800">
                                            List on Boom and Bucket
                                          </Typography>
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          ) : (
            <Spinner />
          )}
        </div>
      </Container>
      {actionModal}

      <HourTrackingModal
        productId={product?.id}
        unitId={unitHourTrackingId}
        isOpen={isHourTrackingOpen}
        close={() => setIsHourTrackingOpen(false)}
      />
    </div>
  );
};

const ProductInventoryUnitModal = ({
  unit,
  close,
  qrCodesEnabled,
  hourTrackingEnabled,
  makeModelEnabled,
  locations,
  makes,
  allowForSale,
  allowForRent,
  onSubmit,
  refreshMakes,
}: {
  unit?: ProductInventoryUnit;
  close: () => void;
  qrCodesEnabled: boolean;
  hourTrackingEnabled?: boolean;
  makeModelEnabled?: boolean;
  locations: Location[];
  makes: Make[];
  allowForSale: boolean;
  allowForRent: boolean;
  onSubmit: (work: UpdateProductUnitRequest) => void;
  refreshMakes: () => void;
}) => {
  const { searchModels, createCustomMake, createCustomModel } = useAPI();
  const [work, setWork] = useState<Partial<UpdateProductUnitRequest>>(
    unit
      ? { ...unit, hourReading: unit?.lastHourReading?.hour }
      : {
          isActive: true,
          isOnOutage: false,
          forRent: allowForRent && !allowForSale ? true : false,
          forSale: !allowForRent && allowForSale ? true : false,
        },
  );
  const [selectedMake, setSelectedMake] = useState<Make | null>(unit?.classification?.make ?? null);
  const [models, setModels] = useState<Model[]>();
  const [selectedModel, setSelectedModel] = useState<Model | null>(unit?.classification?.model ?? null);
  const [selectedYear, setSelectedYear] = useState<number | undefined>(unit?.classification?.year);
  const [showAddCustomTaxonomy, setShowAddCustomTaxonomy] = useState<'make' | 'model' | undefined>();

  const [customMakeName, setCustomMakeName] = useState<string>();
  const [customModel, setCustomModel] = useState<{ name: string; yearsManufactured: string }>({
    name: '',
    yearsManufactured: '',
  });

  useEffect(() => {
    const updateMakeModel = async () => {
      if (selectedMake) {
        const _models = await searchModels(selectedMake.id);
        setSelectedModel(_models?.find((m) => m.id === unit?.classification?.model.id) ?? null);
        setSelectedYear(unit?.classification?.year);
        setModels(_models);
      } else {
        setModels(undefined);
        setSelectedModel(null);
      }
    };
    updateMakeModel();
  }, [selectedMake]);
  useEffect(() => {
    if (!selectedModel) {
      setSelectedYear(undefined);
    }
  }, [selectedModel]);

  const resetCustomTaxonomy = () => {
    setShowAddCustomTaxonomy(undefined);
    setCustomMakeName('');
    setCustomModel({ name: '', yearsManufactured: '' });
  };

  const saveCustomMake = async () => {
    if (!customMakeName) {
      return;
    }
    try {
      const newMake = await createCustomMake({ id: '', name: customMakeName, displayName: customMakeName });
      toast('Make created successfully!', 'success');
      resetCustomTaxonomy();
      refreshMakes();
      setSelectedMake(newMake);
    } catch (err: any) {
      const errMsg = err.status === 400 && err.message ? err.message.msg ?? err.message : 'Error creating make!';
      toast(errMsg, 'error');
    }
  };

  const saveCustomModel = async () => {
    if (!customModel.name) {
      return;
    }
    try {
      const newModel = await createCustomModel(selectedMake!.id, {
        name: customModel.name,
        displayName: customModel.name,
        makeId: selectedMake!.id,
        yearsManufactured: [],
      });
      toast('Model created successfully!', 'success');
      resetCustomTaxonomy();
      searchModels(selectedMake!.id).then((models) => setModels(models));
      setSelectedModel(newModel);
    } catch (err: any) {
      const errMsg = err.status === 400 && err.message ? err.message.msg ?? err.message : 'Error creating model!';
      toast(errMsg, 'error');
    }
  };

  return (
    <Modal isOpen={true} close={close} title={unit ? 'Edit Unit' : 'Add Unit'}>
      <div className="product-unit-form">
        {showAddCustomTaxonomy === 'make' ? (
          <div className="make-form">
            <div className="input-group">
              <label>Make Name</label>
              <input value={customMakeName} onChange={(v) => setCustomMakeName(v.target.value)} />
            </div>
            <div className="button-group">
              <Button className="button-secondary" onClick={() => resetCustomTaxonomy()}>
                Cancel
              </Button>
              <Button
                disabled={!customMakeName}
                className="button-primary"
                onClick={() => {
                  saveCustomMake();
                  resetCustomTaxonomy();
                }}
              >
                Save
              </Button>
            </div>
          </div>
        ) : showAddCustomTaxonomy === 'model' ? (
          <div className="model-form">
            <div className="input-group">
              <label>Make</label>
              <Select
                options={makes}
                value={selectedMake}
                onChange={(o) => setSelectedMake(o)}
                getOptionValue={(o) => `${o.id}`}
                getOptionLabel={(o) => o.name}
                isClearable={true}
                isMulti={false}
                isSearchable={true}
              />
            </div>
            <div className="input-group">
              <label>Model Name</label>
              <input
                value={customModel?.name}
                onChange={(v) =>
                  setCustomModel((m) => {
                    return { ...m, name: v.target.value };
                  })
                }
              />
            </div>
            <div className="button-group">
              <Button className="button-secondary" onClick={() => resetCustomTaxonomy()}>
                Cancel
              </Button>
              <Button
                disabled={!customModel.name}
                className="button-primary"
                onClick={() => {
                  saveCustomModel();
                  resetCustomTaxonomy();
                }}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="product-unit-form">
            <form>
              {allowForSale && allowForRent && (
                <div className="flex gap-5">
                  <div className="flex gap-1 items-center">
                    <input
                      type="checkbox"
                      checked={work.forRent}
                      onChange={(v) => setWork((w) => ({ ...w, forRent: v.target.checked }))}
                    />{' '}
                    <label>For Rent</label>
                  </div>
                  <div className="flex gap-1 items-center">
                    <input
                      type="checkbox"
                      checked={work.forSale}
                      onChange={(v) => setWork((w) => ({ ...w, forSale: v.target.checked }))}
                    />{' '}
                    <label>For Sale</label>
                  </div>
                </div>
              )}
              <div className="input-group">
                <label>Unit Number</label>
                <input
                  value={work.unitNumber}
                  onChange={(v) =>
                    setWork((w) => {
                      return { ...w, unitNumber: v.target.value };
                    })
                  }
                  data-cy="unit-number"
                />
              </div>

              <div className="input-group">
                <label>Serial Number</label>
                <input
                  value={work.serialNumber}
                  onChange={(v) =>
                    setWork((w) => {
                      return { ...w, serialNumber: v.target.value };
                    })
                  }
                  data-cy="serial-number"
                />
              </div>

              {makeModelEnabled && (
                <div>
                  <div className="input-group">
                    <label>Make</label>
                    <Select
                      options={makes}
                      value={selectedMake}
                      onChange={(o) => setSelectedMake(o)}
                      getOptionValue={(o) => `${o.id}`}
                      getOptionLabel={(o) => o.name}
                      isClearable={true}
                      isMulti={false}
                      isSearchable={true}
                    />
                    <div
                      className="flex flex-end items-center self-right p-1 underline"
                      onClick={() => setShowAddCustomTaxonomy('make')}
                    >
                      <span className="flex gap-1 cursor-pointer">
                        <Icon icon="Plus" size="small" />
                        <Typography type="text-xs">Add Make</Typography>
                      </span>
                    </div>
                  </div>

                  <div className="input-group">
                    <label>Model</label>
                    <Select
                      isDisabled={!selectedMake}
                      options={models}
                      value={selectedModel}
                      onChange={(o) => setSelectedModel(o)}
                      getOptionValue={(o) => `${o.id}`}
                      getOptionLabel={(o) => o.name}
                      isClearable={true}
                      isMulti={false}
                      isSearchable={true}
                    />
                    <div
                      className="flex flex-end items-center self-right p-1 underline"
                      onClick={() => setShowAddCustomTaxonomy('model')}
                    >
                      <span className={`flex gap-1 ${selectedMake ? 'cursor-pointer' : 'cursor-disabled'}`}>
                        <Icon icon="Plus" />
                        <Typography type="text-xs">Add Model</Typography>
                      </span>
                    </div>
                  </div>

                  <div className="input-group">
                    <label>Year</label>
                    {selectedModel?.yearsManufactured?.length && selectedModel.yearsManufactured[0] !== 0 ? (
                      <Select
                        options={selectedModel.yearsManufactured.map((y) => ({ label: y.toString(), value: y }))}
                        value={selectedYear ? { label: selectedYear?.toString(), value: selectedYear } : undefined}
                        onChange={(o) => setSelectedYear(o?.value)}
                        isClearable={false}
                        isMulti={false}
                        isDisabled={!selectedModel}
                        isSearchable={false}
                      />
                    ) : (
                      <input
                        disabled={!selectedModel}
                        type="number"
                        value={!selectedModel ? '' : selectedYear}
                        onChange={(v) => setSelectedYear(parseFloat(v.target.value))}
                      />
                    )}
                  </div>
                </div>
              )}

              <div className="input-group">
                <label>Purchase Price</label>
                <input
                  type="number"
                  value={work.purchasePrice || ''}
                  onChange={(v) =>
                    setWork((w) => {
                      return { ...w, purchasePrice: parseFloat(v.target.value) };
                    })
                  }
                  data-cy="purchase-price"
                />
              </div>

              <div className="input-group">
                <label>Purchased At</label>
                <DatePicker
                  value={
                    work.purchasedAt ||
                    DateTime.local({ zone: locations.find((l) => l.id === work.locationId)?.timezone })
                  }
                  onChange={(d) =>
                    d &&
                    setWork((w) => {
                      return { ...w, purchasedAt: d };
                    })
                  }
                />
              </div>

              {qrCodesEnabled && (
                <div className="input-group">
                  <label>QR Code</label>
                  <input
                    value={work.qrCode}
                    onChange={(v) =>
                      setWork((w) => {
                        return { ...w, qrCode: v.target.value };
                      })
                    }
                  />
                </div>
              )}

              {hourTrackingEnabled && (
                <div className="input-group">
                  <label>Machine Hours</label>
                  <input
                    value={work.hourReading}
                    type="number"
                    onChange={(v) => {
                      setWork((w) => {
                        return { ...w, hourReading: parseFloat(v.target.value) };
                      });
                    }}
                  />
                </div>
              )}

              <div className="input-group">
                <label>Location</label>
                <Select
                  options={locations || []}
                  value={locations.find((l) => l.id === work.locationId)}
                  onChange={(o) =>
                    setWork((w) => {
                      return { ...w, locationId: o!.id };
                    })
                  }
                  getOptionValue={(o) => `${o.id}`}
                  getOptionLabel={(o) => o.name}
                  isClearable={false}
                  isMulti={false}
                  id="location-select"
                  classNamePrefix="location"
                />
              </div>
              <div className="button-group">
                <Button className="button-secondary" onClick={close}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    (!work.unitNumber && !work.serialNumber) ||
                    !work.locationId ||
                    (!!selectedMake && (!selectedModel || !selectedYear))
                  }
                  className="button-primary"
                  onClick={() => {
                    //@ts-expect-error work
                    onSubmit({
                      ...work,
                      modelYear:
                        selectedModel && selectedYear ? { modelId: selectedModel.id, year: selectedYear } : undefined,
                    });
                  }}
                  data-cy="unit-save"
                >
                  Save
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Modal>
  );
};

const GenerateQRCodesInput = () => {
  const { contextPath } = useContext(TenantContext);
  const [size, setSize] = useState<string>('100');

  return (
    <div className="qr-code-control">
      <select
        name="qrsize"
        className="small"
        title="Active"
        id="qrsize"
        value={size}
        onChange={(e) => setSize(e.target.value)}
      >
        <option value="100">Small (1 in. X 1 in.)</option>
        <option value="150">Medium (1.5 in. X 1.5 in.)</option>
        <option value="200">Large (2 in.X 2 in.)</option>
      </select>
      <button
        onClick={() => {
          const url = `${contextPath}legacy-app/manager/products/generate_qrcodes?size=${size}`;
          window.open(url, '_blank')?.focus();
        }}
        className="button-primary sm"
      >
        Generate QR Codes
      </button>
    </div>
  );
};

const HourTrackingModal = ({
  productId,
  unitId,
  isOpen,
  close,
}: {
  productId?: number;
  unitId?: number;
  isOpen: boolean;
  close: () => void;
}) => {
  const { getUnitHourReadings } = useAPI();

  const [history, setHistory] = useState<HourReading[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const getHistory = async () => {
      if (!unitId || !productId) return;

      setIsLoading(true);
      const unitHistory = await getUnitHourReadings(productId, unitId);

      setHistory(unitHistory);
      setIsLoading(false);
    };

    getHistory();
  }, [unitId]);

  return (
    <Modal title="Hour Reading History" isOpen={isOpen} close={close}>
      {isLoading ? (
        <Spinner />
      ) : history.length ? (
        <div className="product-unit-history">
          {history.map((record) => {
            return (
              <div key={record.id} className="hour-tracking-record">
                <div className="line-wrapper">
                  <div className="circle"></div>
                </div>

                <div className="record-content">
                  <span className="text-primary">
                    {record.hour} - {record.source.split(/(?=[A-Z])/).join(' ')}
                  </span>
                  . {record.trackedAt.toFormat('ff')}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <span>There is no record for this unit.</span>
      )}
    </Modal>
  );
};

const ListOnBoomAndBucketModal = ({
  productUnit,
  configs,
  location,
  close,
}: {
  productUnit: ProductInventoryUnit;
  configs: Dictionary<Configuration>;
  location: Location;
  close: () => void;
}) => {
  const [data, setData] = useState<Partial<ListOnBoomAndBucketRequest>>({});
  const [address, setAddress] = useState<{
    sellerOfficeAddressLineOne: string;
    sellerOfficeAddressLineTwo?: string;
    sellerOfficeCity?: string;
    sellerOfficeState?: string;
    sellerOfficeZipCode?: string;
  }>({
    sellerOfficeAddressLineOne: location.address.address1,
    sellerOfficeAddressLineTwo: location.address.address2,
    sellerOfficeCity: location.address.city,
    sellerOfficeState: location.address.state,
    sellerOfficeZipCode: location.address.zip,
  });

  const [isAddressGoogleAutogenerate, setAddressGoogleAutogenerate] = useState<boolean>(true);
  const { listOnBoomAndBucket } = useAPI();

  useEffect(() => {
    setData({
      productId: productUnit.productId,
      productUnitId: productUnit.id,
      hours: productUnit.lastHourReading?.hour,
      mileage: undefined,
      sellerCompanyName: configs['CONF_WEBSITE_NAME']?.value,
      sellerContactFirstName: configs['CONF_BOOM_AND_BUCKET_SELLER_CONTACT_FIRST_NAME']?.value,
      sellerContactLastName: configs['CONF_BOOM_AND_BUCKET_SELLER_CONTACT_LAST_NAME']?.value,
      sellerContactEmail: configs['CONF_BOOM_AND_BUCKET_SELLER_CONTACT_EMAIL']?.value,
      sellerContactPhone: configs['CONF_BOOM_AND_BUCKET_SELLER_CONTACT_PHONE']?.value,
    });
  }, [productUnit, configs]);

  const createListing = async () => {
    if (
      !(
        data.hours &&
        ((data.highwayType === HighwayType.OnHighway && data.mileage) || data.highwayType === HighwayType.OffHighway) &&
        data.sellerContactFirstName &&
        data.sellerContactLastName &&
        data.sellerContactEmail &&
        data.sellerContactPhone &&
        address.sellerOfficeAddressLineOne &&
        address.sellerOfficeCity &&
        address.sellerOfficeState &&
        address.sellerOfficeZipCode
      )
    ) {
      toast('Please fill in all required fields', 'error');
      return;
    }
    try {
      await listOnBoomAndBucket({ ...data, ...address } as ListOnBoomAndBucketRequest);
      toast('Successfully listed on Boom and Bucket', 'success');
      close();
    } catch (e: any) {
      const errMsg = e.status === 400 && e.message ? e.message.msg ?? e.message : 'Error listing on Boom and Bucket!';
      toast(errMsg, 'error');
    }
  };

  return (
    <Modal className="max-h-[725px] overflow-scroll" isOpen={true} close={close} title={'List on Boom and Bucket'}>
      <div className="list-on-boom-and-bucket-modal">
        <form className="grid gap-y-3">
          <Typography type="text-lg" weight="bold">
            {productUnit.classification!.year} {productUnit.classification!.make.displayName}{' '}
            {productUnit.classification!.model.displayName}
          </Typography>
          <Typography type="text-lg" weight="bold">
            Serial Number: {productUnit.serialNumber}
          </Typography>
          <div className="flex gap-2">
            <Field
              isRequired={true}
              label="Operating Hours Meter Reading"
              type="number"
              maximumFractionDigits={0}
              value={data.hours}
              onChange={(hours) => setData({ ...data, hours })}
            />
            <Field
              isRequired={data.highwayType === HighwayType.OnHighway}
              label="Mileage"
              type="number"
              maximumFractionDigits={0}
              value={data.mileage}
              onChange={(mileage) => setData({ ...data, mileage })}
            />
          </div>
          <label>Highway Type</label>
          <div className="flex gap-4">
            <div className="flex gap-2">
              <Radio
                name="OnHighway"
                value="On Highway"
                isChecked={data.highwayType === HighwayType.OnHighway}
                onChange={() => setData({ ...data, highwayType: HighwayType.OnHighway })}
              />
              <Typography type="text-sm">On Highway</Typography>
            </div>
            <div className="flex gap-3">
              <Radio
                name="OffHighway"
                value="Off Highway"
                isChecked={data.highwayType === HighwayType.OffHighway}
                onChange={() => setData({ ...data, highwayType: HighwayType.OffHighway })}
              />
              <Typography type="text-sm">Off Highway</Typography>
            </div>
          </div>

          <Typography type="text-lg" weight="semi-bold">
            Seller Info
          </Typography>
          <div className="flex gap-3 w-full">
            <Field
              className="w-full"
              isRequired={true}
              label="First Name"
              type="text"
              value={data.sellerContactFirstName}
              onChange={(v) => setData({ ...data, sellerContactFirstName: v })}
            />
            <Field
              className="w-full"
              isRequired={true}
              label="Last Name"
              type="text"
              value={data.sellerContactLastName}
              onChange={(v) => setData({ ...data, sellerContactLastName: v })}
            />
          </div>
          <div className="flex gap-3 w-full">
            <Field
              className="w-full"
              isRequired={true}
              label="Email"
              type="text"
              value={data.sellerContactEmail}
              onChange={(v) => setData({ ...data, sellerContactEmail: v })}
            />
            <Field
              className="w-full"
              isRequired={true}
              label="Phone"
              type="text"
              value={data.sellerContactPhone}
              onChange={(v) => setData({ ...data, sellerContactPhone: v })}
            />
          </div>

          <PlacesAutocomplete
            label="Address line 1"
            value={address.sellerOfficeAddressLineOne}
            dataCy="google_address"
            onChange={(address1: string) => {
              setAddress({ ...address, sellerOfficeAddressLineOne: address1 });
            }}
            onPredictionSelected={(generatedAddress) => {
              setAddress({
                ...address,
                sellerOfficeAddressLineOne: generatedAddress.address1,
                sellerOfficeZipCode: generatedAddress.postcode !== '' ? generatedAddress.postcode : undefined,
                sellerOfficeCity: generatedAddress.city !== '' ? generatedAddress.city : undefined,
                sellerOfficeState: generatedAddress.state !== '' ? generatedAddress.state : undefined,
              });

              setAddressGoogleAutogenerate(true);
            }}
            isRequired={true}
            placeholder=""
          />

          <Field
            type="text"
            label="Address line 2"
            value={address.sellerOfficeAddressLineTwo}
            onChange={(address2: string) => {
              setAddress({ ...address, sellerOfficeAddressLineTwo: address2 });
            }}
            isRequired={false}
          />
          <Field
            type="text"
            label="State/Province name"
            value={address.sellerOfficeState}
            onChange={(state: string) => {
              setAddress({ ...address, sellerOfficeState: state });
            }}
            isRequired={true}
            disabled={isAddressGoogleAutogenerate}
            description="After setting Address Line 1 this will be automatically updated"
          />

          <Field
            type="text"
            label="City name"
            value={address.sellerOfficeCity}
            onChange={(city: string) => {
              setAddress({ ...address, sellerOfficeCity: city });
            }}
            isRequired={true}
            disabled={isAddressGoogleAutogenerate}
            description="After setting Address Line 1 this will be automatically updated"
          />

          <Field
            type="text"
            label="Postcode"
            value={address.sellerOfficeZipCode}
            onChange={(zip: string) => {
              setAddress({ ...address, sellerOfficeZipCode: zip });
            }}
            isRequired={true}
            disabled={isAddressGoogleAutogenerate}
            description="After setting Address Line 1 this will be automatically updated"
          />
        </form>
        <div className="button-group mt-3">
          <Button className="button-secondary" onClick={close}>
            Cancel
          </Button>
          <Button className="button-primary" onClick={createListing}>
            Create Listing
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const BoomAndBucketListingDetails = ({
  productUnit,
  close,
}: {
  productUnit: ProductInventoryUnit;
  close: () => void;
}) => {
  const [listing, setListing] = useState<EquipmentListingResponse>();
  const { getBoomAndBucketListingsByProductUnitId } = useAPI();

  const getListing = useCallback(
    debounce(async function (productUnitId: number) {
      try {
        const _listing = await getBoomAndBucketListingsByProductUnitId(productUnitId);
        setListing(_listing);
      } catch (e) {
        toast('Error loading listing details', 'error');
        close();
      }
    }, 600),
    [],
  );
  useEffect(() => {
    getListing(productUnit.id);
  }, [productUnit.id]);
  const toDisplayStateText = (state: DisplayState) => {
    switch (state) {
      case DisplayState.NotDisplayed:
        return 'Not Displayed';
      case DisplayState.OnMarketplace:
        return 'On Marketplace';
      case DisplayState.OnMarketplaceAsInactive:
        return 'On Marketplace as Inactive';
      default:
        return 'Unknown';
    }
  };

  return (
    <Modal isOpen={true} close={close} title={'Boom and Bucket Listing'}>
      <div className="view-boom-and-bucket-listing-modal">
        <Typography type="text-lg" weight="bold">
          {productUnit.classification!.year} {productUnit.classification!.make.displayName}{' '}
          {productUnit.classification!.model.displayName}
        </Typography>
        <Typography type="text-lg" weight="bold">
          Serial Number: {productUnit.serialNumber}
        </Typography>
        {listing?.activeListing && (
          <Typography type="text-md">Status: {toDisplayStateText(listing.activeListing?.displayState)}</Typography>
        )}
      </div>
    </Modal>
  );
};
